import React from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import Checkbox from '@mui/material/Checkbox';
import {styled} from '@mui/system';

import {concatUrl} from '../helpers/url';
import {getCall, postCall} from '../helpers/request';
import GlobalContext from '../helpers/context';
import LinkButton from '../component/atoms/LinkButton';
import ProgressSpinner from '../component/atoms/ProgressSpinner';
import ResearcherId from '../component/molecule/ResearcherId';
import {checkUserRoles, getUser} from '../helpers/authentication';
import {autoPluralize} from '../helpers/pluralizer';
import {showActiveYears} from '../helpers/active_years';
import Linkout from '../component/molecule/Linkout';
import GoogleLink from '../component/molecule/GoogleLink';
import DoiLink from '../component/molecule/DoiLink';
import {THEME} from '../theme';
import Box from "@mui/material/Box";
import CustomAccordion from "../component/molecule/CustomAccordion";


const StyledSuspiciousDeletes = styled(Paper)(({theme}) =>({
    paddingLeft: theme.custom.paper.side_space,
    paddingRight: theme.custom.paper.side_space,
    paddingTop: theme.custom.paper.top_space,
    paddingBottom: theme.custom.paper.bottom_space,
    marginBottom: 30,
    minWidth: theme.custom.min_width
}));

const SuspiciousDeleteTitle = styled(Typography)({
    marginBottom: 20,
    fontWeight: 500,
    fontSize: 20
});

const SuspiciousDeletesHeader = styled(Grid)(({theme}) =>({
    padding: '10px 41px 10px 16px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontWeight: theme.custom.font.bold
}));

const StyledSuspiciousDeleteItem = styled(Grid)(({theme}) =>({
    fontSize: theme.custom.font.medium
}));

const SuspiciousDeleteDetails = styled('div')(({theme}) =>({
    fontSize: theme.custom.font.medium
}));

const StyledDocumentMetadata = styled('p')({
    margin: 0,
    padding: 0
});


class SuspiciousDeletes extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            suspicious_deletes: undefined,
            suspicious_delete_count: '',
            suspicious_deletes_details: {},
            next_page: undefined,
            anchorEl: null,
            user: null,
            show_ignored: false
        };

        this.style = {
            suspicious_delete_name_button: {
                color: THEME.palette.primary.dark,
                fontSize: THEME.typography.fontSize,
                padding: 0,
                minWidth: 0,
                textTransform: 'none'
            }
        };

        this.is_mounted = false;
        this.accepted_roles = ['admin', 'manager', 'reviewer'];
        this.get_suspicious_deletes_url = concatUrl(window.ROOT_URL, 'api', 'suspicious_deletes');
        this.ignore_suspicious_delete_url = concatUrl(window.ROOT_URL, 'api', 'suspicious_deletes', 'ignore');
    }

    componentDidMount(){
        this.is_mounted = true;
        this.props.updatedCallback();
        this.loadSuspiciousDeletes(this.state.show_ignored);
        getUser((user) => {
            if(this.is_mounted) {
                this.setState({user: user});
            }
        });
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    componentDidUpdate(_, prevState){
        this.props.updatedCallback();
        this.context.checkUserAccess(this.conditionCallback);
        if (prevState.next_page !== this.state.next_page && this.state.next_page){
            this.register_lazy_loading();
        }
    }

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };

    stopPropagation = (event) => {
        event.stopPropagation();
    };

    register_lazy_loading = () => {
        let options = {
          root: null,
          threshold: 0.5
        };
        if (this.state.next_page){
            let observer = new IntersectionObserver(this.loadNextPage, options);
            let target = document.querySelector('#nextPage');
            observer.observe(target);
        }
    };

    loadNextPage = (entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && this.state.next_page && !this.state.loading_next_page) {
                this.loadNextPageDetails();
            }
        });
    };

    // +++ load suspicious deletes data +++++++++++++++++++++++++++++++++++++++++++++++
    // suspicious_deletes contains only the first set of suspicious deletes CP. Next block will be
    // loaded by loadNextPageDetails triggered by lazy loading.

    loadSuspiciousDeletesFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load suspicious deletes.');
    };

    loadSuspiciousDeletesDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else{
            this.setState({
                suspicious_deletes: data.suspicious_deletes,
                suspicious_deletes_count: data.suspicious_deletes_count,
                next_page: data.next_page
            });
        }
    };

    loadSuspiciousDeletes = (show_ignored) => {
        postCall(this.get_suspicious_deletes_url, {'show_ignored': show_ignored}, this.loadSuspiciousDeletesDone, this.loadSuspiciousDeletesFailed);
    };

    // +++ add/remove a suspicious delete item to the ignore list ++++++++++++++++++++++++++++++++++

    updateIgnoreListFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not update the list of ignored suspicious deletes.');
    };

    updateIgnoreListDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else{
            this.loadSuspiciousDeletes(this.state.show_ignored);
        }
    };

    updateIgnoreList = expanded_sp => event => {
        let suspicious_deletes = this.state.suspicious_deletes;
        suspicious_deletes.map((_expanded_sp) => {
            if (_expanded_sp.suspicious_delete.id === expanded_sp.suspicious_delete.id &&
                _expanded_sp.suspicious_delete.doc_id === expanded_sp.suspicious_delete.doc_id){
                _expanded_sp.suspicious_delete.confirmed = !_expanded_sp.suspicious_delete.confirmed;
            }
        });
        this.setState({'suspicious_deletes': suspicious_deletes});

        const data = {
            curation_profile_id: expanded_sp.curation_profile.id,
            resource_id: expanded_sp.suspicious_delete.doc_id,
            ignored: event.currentTarget.checked
        };
        postCall(this.ignore_suspicious_delete_url, data, this.updateIgnoreListDone, this.updateIgnoreListFailed);
    };


    // +++ load next page (next block of curation profiles) +++++++++++++++++++++++++++++++++++++++

    loadNextPageFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load next page.');
        this.setState({loading_next_page: false});
    };

    loadNextPageDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else{
            let suspicious_deletes = this.state.suspicious_deletes;
            this.setState({
                suspicious_deletes: suspicious_deletes.concat(data.suspicious_deletes),
                next_page: data.next_page,
                loading_next_page: false
            });
        }
    };

    loadNextPageDetails = () => {
        this.setState({loading_next_page: true});
        getCall(this.state.next_page, this.loadNextPageDone, this.loadNextPageFailed);
    };

    updateShowIgnoredFlag = (event) => {
        let show_ignored = event.currentTarget.checked;
        this.setState({
            'show_ignored': show_ignored,
            'suspicious_deletes': undefined
        });
        this.loadSuspiciousDeletes(show_ignored);
    }

    curationProfileLink = (expanded_sp) => {
      if(expanded_sp.suspicious_delete.first_workspace_id){
          return (
              <LinkButton text={expanded_sp.curation_profile.full_name} linkStyle="link"
                          linkData={{
                              pathname: '/workspaces/curation_profiles/details',
                              search: `?id=${expanded_sp.curation_profile.id}&workspace_id=${expanded_sp.suspicious_delete.first_workspace_id}&default_tab=5&doc_id=${expanded_sp.suspicious_delete.doc_id}`
                          }}
                          addClass={this.style.suspicious_delete_name_button}
                          dataBt="curation_profile_details_button"/>
          );
      }
      return (
          <Tooltip title="You have no access to the workspace(s) this profile is assigned to." placement="bottom-start">
              <div>{expanded_sp.curation_profile.full_name}</div>
          </Tooltip>
      );
    };

    documentDetails = (suspicious_delete) => {
        if (suspicious_delete.document === null){
            return (<div>n.a.</div>)
        }
        if (suspicious_delete.resource_type === 'G'){
            return (
                <div>
                    <Link
                        target="_blank"
                        rel="noreferrer"
                        onClick={this.stopPropagation}
                        href={`https://app.dimensions.ai/details/grant/${suspicious_delete.document.id}`}>
                        {suspicious_delete.document.title}</Link>
                    <StyledDocumentMetadata>{suspicious_delete.document.researcher_list}</StyledDocumentMetadata>
                    <StyledDocumentMetadata>{showActiveYears(suspicious_delete.document.active_year)}; {suspicious_delete.document.funding_org_name}</StyledDocumentMetadata>
                    {suspicious_delete.document.linkout && <Linkout url={suspicious_delete.document.linkout} label="fulltext" />}
                </div>
            );
        }
        return (
            <div>
                <Link
                        target="_blank"
                        rel="noreferrer"
                        onClick={this.stopPropagation}
                        href={`https://app.dimensions.ai/details/publication/${suspicious_delete.document.id}`}>
                        {suspicious_delete.document.html_escaped_title}</Link>
                    <StyledDocumentMetadata>{suspicious_delete.document.author_list}</StyledDocumentMetadata>
                    <StyledDocumentMetadata>
                        {suspicious_delete.document.pub_year}
                        {suspicious_delete.document.pub_year && suspicious_delete.document.journal_title_original && ', '}
                        {suspicious_delete.document.journal_title_original}
                    </StyledDocumentMetadata>
                    <GoogleLink query={suspicious_delete.document.title} />{suspicious_delete.document.doi && <span> | <DoiLink doi={suspicious_delete.document.doi} /></span>}
            </div>
        );
    }

    curationProfileList(){
        if(this.state.suspicious_deletes) {
            return (
                <div>
                    {this.state.suspicious_deletes.map((expanded_sp, index) =>
                        <CustomAccordion
                        key={index}
                        dataBt={"suspicious_delete_item"}
                        summary={
                            <Box sx={{ flexGrow: 1 }}>
                                <StyledSuspiciousDeleteItem container spacing={2}>
                                    <Grid size={2}>
                                        {this.curationProfileLink(expanded_sp)}
                                    </Grid>
                                    <Grid size={2}>
                                        {expanded_sp.suspicious_delete.former_last_name}, {expanded_sp.suspicious_delete.former_first_name}
                                    </Grid>
                                    <Grid size={5}>
                                        {this.documentDetails(expanded_sp.suspicious_delete)}
                                    </Grid>
                                    <Grid size={2}>
                                        <ResearcherId id={expanded_sp.curation_profile.researcher_id}/>
                                    </Grid>
                                    <Grid size={1}>
                                        <Checkbox
                                            onClick={this.stopPropagation}
                                            checked={expanded_sp.suspicious_delete.confirmed}
                                            onChange={this.updateIgnoreList(expanded_sp)}
                                        />
                                    </Grid>
                                </StyledSuspiciousDeleteItem>
                            </Box>
                        }
                        content={
                            <SuspiciousDeleteDetails>
                                <p>deletion
                                    time: <span>{expanded_sp.suspicious_delete.deletion_time}</span>
                                </p>
                                <p>Workspaces:</p>
                                <ul>
                                    {expanded_sp.suspicious_delete.workspaces.map((workspace, index) =>
                                        <li key={index}>{workspace.name}</li>
                                    )}
                                </ul>
                            </SuspiciousDeleteDetails>
                        }
                    />)}
                    {this.state.next_page && <div id="nextPage"><ProgressSpinner/></div>}
                </div>
            );
        }
    }

    render() {
        return (
            <GlobalContext.Consumer>{() => (
                <StyledSuspiciousDeletes data-bt="suspicious_deletes">
                    <Grid container justifyContent="space-between">
                        <Grid>
                            <SuspiciousDeleteTitle>
                                {autoPluralize(this.state.suspicious_deletes_count, 'Suspicious delete')}
                            </SuspiciousDeleteTitle>
                        </Grid>
                        <Grid>
                            <Checkbox
                                checked={this.state.show_ignored}
                                onChange={this.updateShowIgnoredFlag}
                            /><span>include verified deletes</span>
                        </Grid>
                    </Grid>
                    <SuspiciousDeletesHeader container spacing={2}>
                        <Grid size={2}>
                            Name
                        </Grid>
                        <Grid size={2}>
                            Former name
                        </Grid>
                        <Grid size={5}>
                            Source
                        </Grid>
                        <Grid size={2}>
                            Researcher ID
                        </Grid>
                        <Grid size={1}>
                            Verify delete
                        </Grid>
                    </SuspiciousDeletesHeader>
                    {!this.state.suspicious_deletes && <ProgressSpinner />}
                    {this.curationProfileList()}
                </StyledSuspiciousDeletes>
            )}
            </GlobalContext.Consumer>
        );
    }
}


SuspiciousDeletes.contextType = GlobalContext;

SuspiciousDeletes.propTypes = {
    updatedCallback: PropTypes.func
};

export default SuspiciousDeletes;
